/**
 * Who is signed in (GET /api/v1/me) and sign-out (POST /auth/logout). Staff sign in only through the POS
 * menu "แชทรวม" (POS signs a one-time link → /auth/sso sets an HttpOnly cookie → back to `/`). The one exception is the
 * Meta App Review reviewer at /login while the owner has it switched on (./reviewer.ts, components/ReviewerSignIn.vue).
 * Mock mode: always signed in as the mock team's first member.
 */
import { getApi, useMock } from '.'

export type StaffRole = 'owner' | 'head' | 'staff'

export interface Me {
  id: string
  name: string
  initial: string
  role: StaffRole
  pos_staff_id: string | null
  /** Signed in at /login (Meta App Review reviewer, api/app/reviewer.py) — not through the POS. */
  reviewer?: boolean
}

/** Why the sign-in screen is showing — `?sso=` set by /auth/sso, or what happened in this tab. */
export type SignedOutReason = 'expired' | 'used' | 'invalid' | 'inactive' | 'off' | 'logged_out' | 'session' | null

export const POS_SSO_URL = (import.meta.env.VITE_POS_SSO_URL ?? '').trim() || 'https://cleanfit.cloud/beta/pos/chat_sso.php'

const REASONS = new Set(['expired', 'used', 'invalid', 'inactive', 'off'])

export function parseReason(search: string): SignedOutReason {
  const v = new URLSearchParams(search).get('sso')
  return v && REASONS.has(v) ? (v as SignedOutReason) : null
}

/** One calm line under the heading of the sign-in screen. */
export function reasonText(r: SignedOutReason): string {
  switch (r) {
    case 'expired':
    case 'used':
      return 'ลิงก์เข้าระบบใช้ได้ครั้งเดียวภายใน 1 นาที กดเมนูแชทรวมใน POS อีกครั้ง'
    case 'inactive':
      return 'บัญชีนี้ถูกปิดการใช้แชท ติดต่อผู้จัดการหรือเจ้าของร้าน'
    case 'off':
    case 'invalid':
      return 'เข้าระบบไม่สำเร็จ กดเมนูแชทรวมใน POS อีกครั้ง ถ้ายังไม่ได้ให้แจ้งผู้ดูแลระบบ'
    case 'logged_out':
      return 'ออกจากระบบแล้ว'
    case 'session':
      return 'หมดเวลาการเข้าระบบ กดเมนูแชทรวมใน POS เพื่อเข้าใหม่'
    default:
      return 'ระบบแชทเปิดได้จากเมนูใน POS เท่านั้น ไม่ต้องใส่รหัสผ่านซ้ำ'
  }
}

export const ROLE_LABEL: Record<StaffRole, string> = { owner: 'เจ้าของ', head: 'ผู้จัดการ', staff: 'พนักงาน' }

async function call(method: string, path: string): Promise<Response> {
  const base = (import.meta.env.VITE_API_BASE ?? '').trim().replace(/\/+$/, '')
  return fetch(`${base}${path}`, { method, credentials: 'same-origin', headers: { Accept: 'application/json' } })
}

/** Mock mode only: act as another role with localStorage `cf-chat.mock-role` = owner | head (default staff). */
function mockRole(): StaffRole {
  try {
    const r = localStorage.getItem('cf-chat.mock-role')
    if (r === 'owner' || r === 'head') return r
  } catch {
    /* no storage (tests) */
  }
  return 'staff'
}

/** The signed-in staff member, or null when not signed in. Network/server errors throw. */
export async function fetchMe(): Promise<Me | null> {
  if (useMock) {
    const b = await getApi().bootstrap()
    return { id: b.me.id, name: b.me.name, initial: b.me.initial, role: mockRole(), pos_staff_id: null }
  }
  const res = await call('GET', '/api/v1/me')
  if (res.status === 401) return null
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as Me
}

export async function signOut(): Promise<void> {
  if (useMock) return
  const res = await call('POST', '/auth/logout')
  if (!res.ok && res.status !== 401) throw new Error(`HTTP ${res.status}`)
}
